<div class="wrapper-billing bg-slate-50 min-h-screen">
  <!-- Original Header / Breadcrumb -->
  <div class="section-title fixed row w-full flex z-[8]">
    <div class="section-main section-btn">
      <button
        routerLink="/dashboard/product-manager"
        class="btn btn-back" [attr.aria-label]="'Back' | translate">
        <i class="fi-rr-arrow-alt-left"></i>
      </button>
    </div>

    <div class="inline-flex items-end">
      <div class="title" [attr.data-parent]="'Products' | translate">{{ "Update Product" | translate }}</div>
      <button
        class="bg-indigo-500 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded transition ease-in-out duration-300 fixed right-3"
        [class]="productIsSaving ? '!opacity-50 pointer-events-none' : ''"
        (click)="saveCustomProduct($event)"
      >
        {{ "Save" | translate }}
      </button>
    </div>
  </div>

  <!-- Main Body Content -->
  <div class="section-main pt-20 pb-16">
    <!-- Loading State -->
    <div class="row" *ngIf="productIsLoading; else loadedContent">
      <div class="loading-spinner col-12 mt-12 py-12 text-center">
        <mat-spinner class="mx-auto" [diameter]="50"></mat-spinner>
      </div>
    </div>

    <!-- Loaded Content -->
    <ng-template #loadedContent>
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
        <!-- Modern 2-Column Layout (Main 2/3 + Sidebar 1/3) -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

          <!-- LEFT COLUMN (Main Content - 2/3 Width) -->
          <div class="lg:col-span-2 space-y-6">

            <!-- CARD 1: Product Information & Tabbed Editors -->
            <div class="bg-white rounded-xl border border-slate-200/80 shadow-xs overflow-hidden">
              <div class="p-5 sm:p-6 border-b border-slate-100">
                <div class="flex items-center gap-2.5 mb-4">
                  <div class="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                    <i class="fi fi-rr-document text-base"></i>
                  </div>
                  <div>
                    <h2 class="text-base font-bold text-slate-800">{{ "Product Details" | translate }}</h2>
                    <p class="text-xs text-slate-500">{{ "Information displayed in your online storefront." | translate }}</p>
                  </div>
                </div>

                <!-- Product Name -->
                <div>
                  <label for="product-name" class="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                    {{ "Product Name" | translate }} <span class="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="product-name"
                    id="product-name"
                    class="h-10 border border-slate-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg px-3.5 w-full bg-slate-50/50 text-slate-800 text-sm font-medium transition"
                    [(ngModel)]="productData.product_name"
                    (ngModelChange)="onProductNameChange($event)"
                    placeholder="{{ 'Enter product name...' | translate }}"
                  />
                </div>
              </div>

              <!-- Hidden Transport Form for Trumbowyg Image Selector -->
              <div class="trumbowyg-media-selector d-none">
                <form
                  id="trumbowyg-media-selector-event-transport"
                  (setInsertImageCallback)="handleSetInsertImageCallback($event)"
                >
                  <media-selector
                    id="media-selector-embed"
                    [(mediaID)]="editorMediaPhotoId"
                    [(imgURL)]="editorImgURL"
                    (mediaChanged)="handleTrumbowygMediaChanged($event)"
                    class="d-none"
                  ></media-selector>
                </form>
                <textarea class="text-textarea d-none"></textarea>
              </div>

              <!-- Tabbed Rich-Text Editors Container -->
              <div class="p-5 sm:p-6 bg-slate-50/30">
                <div class="flex items-center justify-between mb-3">
                  <span class="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    {{ "Content & Descriptions" | translate }}
                  </span>
                </div>

                <!-- Tab Navigation Header -->
                <div class="editor-tabs-container flex flex-wrap gap-1 mb-0 border-b border-slate-200 bg-slate-100/70 p-1.5 rounded-t-xl">
                  <button
                    type="button"
                    class="tab-btn px-4 py-2 text-xs font-semibold rounded-lg transition flex items-center gap-1.5"
                    [class.active-tab]="activeContentTab === 'desc'"
                    [class.bg-white]="activeContentTab === 'desc'"
                    [class.text-indigo-600]="activeContentTab === 'desc'"
                    [class.shadow-xs]="activeContentTab === 'desc'"
                    [class.text-slate-600]="activeContentTab !== 'desc'"
                    (click)="setActiveContentTab('desc')"
                  >
                    <i class="fi fi-rr-edit"></i>
                    <span>{{ "Description" | translate }}</span>
                  </button>

                  <button
                    type="button"
                    class="tab-btn px-4 py-2 text-xs font-semibold rounded-lg transition flex items-center gap-1.5"
                    [class.active-tab]="activeContentTab === 'overview'"
                    [class.bg-white]="activeContentTab === 'overview'"
                    [class.text-indigo-600]="activeContentTab === 'overview'"
                    [class.shadow-xs]="activeContentTab === 'overview'"
                    [class.text-slate-600]="activeContentTab !== 'overview'"
                    (click)="setActiveContentTab('overview')"
                  >
                    <i class="fi fi-rr-eye"></i>
                    <span>{{ "Overview" | translate }}</span>
                  </button>

                  <button
                    type="button"
                    class="tab-btn px-4 py-2 text-xs font-semibold rounded-lg transition flex items-center gap-1.5"
                    [class.active-tab]="activeContentTab === 'direction'"
                    [class.bg-white]="activeContentTab === 'direction'"
                    [class.text-indigo-600]="activeContentTab === 'direction'"
                    [class.shadow-xs]="activeContentTab === 'direction'"
                    [class.text-slate-600]="activeContentTab !== 'direction'"
                    (click)="setActiveContentTab('direction')"
                  >
                    <i class="fi fi-rr-list-check"></i>
                    <span>{{ "Direction Of Use" | translate }}</span>
                  </button>

                  <button
                    type="button"
                    class="tab-btn px-4 py-2 text-xs font-semibold rounded-lg transition flex items-center gap-1.5"
                    [class.active-tab]="activeContentTab === 'ingredient'"
                    [class.bg-white]="activeContentTab === 'ingredient'"
                    [class.text-indigo-600]="activeContentTab === 'ingredient'"
                    [class.shadow-xs]="activeContentTab === 'ingredient'"
                    [class.text-slate-600]="activeContentTab !== 'ingredient'"
                    (click)="setActiveContentTab('ingredient')"
                  >
                    <i class="fi fi-rr-test-tube"></i>
                    <span>{{ "Ingredients" | translate }}</span>
                  </button>
                </div>

                <!-- Tab Panels -->
                <div class="border-x border-b border-slate-200 bg-white rounded-b-xl p-3 shadow-xs">
                  <!-- Tab 1: Product Description -->
                  <div [class.hidden]="activeContentTab !== 'desc'">
                    <div class="flex items-center justify-between mb-2 px-1">
                      <label for="product-desc" class="text-xs font-medium text-slate-600">
                        {{ "Detailed Product Description" | translate }}
                      </label>
                      <button
                        type="button"
                        (click)="
                          showAIProductDesc({
                            product_id: productData.product_id,
                            desc: 'product_description - product ' + productData.product_name
                          })
                        "
                        [ngClass]="enableAIAssistant !== true ? 'disabled' : ''"
                        class="ai-assistant-button"
                      >
                        <i class="fas fa-magic"></i>
                        <span>{{ "AI Assistant" | translate }}</span>
                      </button>
                    </div>
                    <textarea
                      type="text"
                      name="product-desc"
                      id="product-desc"
                      class="border border-slate-200 rounded-lg p-3 w-full bg-slate-50 content-editor-textarea text-sm"
                      [value]="productData.product_desc"
                    ></textarea>
                  </div>

                  <!-- Tab 2: Overview -->
                  <div [class.hidden]="activeContentTab !== 'overview'">
                    <div class="mb-2 px-1">
                      <label for="product-overview" class="text-xs font-medium text-slate-600">
                        {{ "Short Summary / Product Overview" | translate }}
                      </label>
                    </div>
                    <textarea
                      type="text"
                      name="product-overview"
                      id="product-overview"
                      class="border border-slate-200 rounded-lg p-3 w-full bg-slate-50 content-editor-textarea text-sm"
                      [value]="productData.product_overview"
                    ></textarea>
                  </div>

                  <!-- Tab 3: Direction Of Use -->
                  <div [class.hidden]="activeContentTab !== 'direction'">
                    <div class="mb-2 px-1">
                      <label for="product-direction" class="text-xs font-medium text-slate-600">
                        {{ "Instructions & Direction Of Use" | translate }}
                      </label>
                    </div>
                    <textarea
                      type="text"
                      name="product-direction"
                      id="product-direction"
                      class="border border-slate-200 rounded-lg p-3 w-full bg-slate-50 content-editor-textarea text-sm"
                      [value]="productData.direction"
                    ></textarea>
                  </div>

                  <!-- Tab 4: Ingredients -->
                  <div [class.hidden]="activeContentTab !== 'ingredient'">
                    <div class="mb-2 px-1">
                      <label for="product-ingredient" class="text-xs font-medium text-slate-600">
                        {{ "List of Ingredients" | translate }}
                      </label>
                    </div>
                    <textarea
                      type="text"
                      name="product-ingredient"
                      id="product-ingredient"
                      class="border border-slate-200 rounded-lg p-3 w-full bg-slate-50 content-editor-textarea text-sm"
                      [value]="
                        productData?.ingredient?.length !== 0
                          ? productData?.ingredient
                          : productData?.custom_ingredient !== ''
                          ? productData?.custom_ingredient
                          : ''
                      "
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>

            <!-- CARD 2: Product Highlights -->
            <div class="bg-white rounded-xl border border-slate-200/80 shadow-xs p-5 sm:p-6">
              <div class="flex items-center justify-between mb-4">
                <div class="flex items-center gap-2.5">
                  <div class="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                    <i class="fi fi-rr-star text-base"></i>
                  </div>
                  <div>
                    <h2 class="text-base font-bold text-slate-800 flex items-center gap-2">
                      {{ "Product Highlights" | translate }}
                      <span class="text-xs font-normal px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full">
                        {{ getValidProductHighlights().length }}/{{ productHighlightAmount }}
                      </span>
                    </h2>
                    <p class="text-xs text-slate-500">{{ "Highlight key features and qualities to catch customer attention." | translate }}</p>
                  </div>
                </div>
              </div>

              <div class="space-y-3">
                <div *ngIf="getProductHighlightInputs().length">
                  <div
                    class="product-highlight-item mb-2.5"
                    *ngFor="
                      let productHighlight of getProductHighlightInputs();
                      index as i
                    "
                  >
                    <div class="label-container">
                      <label [for]="'product-highlight-value-' + i">
                        {{ "Highlight" | translate }} {{ i + 1 }}
                      </label>
                    </div>
                    <input
                      type="text"
                      maxlength="80"
                      [name]="'product-highlight-value-' + i"
                      [id]="'product-highlight-value-' + i"
                      class="h-10 border border-slate-300 focus:border-indigo-500 rounded-lg px-3 w-full bg-slate-50/50 text-sm text-slate-800 transition"
                      [value]="productHighlight"
                      (change)="handleProductHighlightChange($event, i)"
                      placeholder="{{ 'Product qualities, benefits, or key features...' | translate }}"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- CARD 3: Product Mixture / Variations (Conditional) -->
            <div
              class="bg-white rounded-xl border border-slate-200/80 shadow-xs p-5 sm:p-6"
              *ngIf="productData?.mixture_data"
            >
              <div class="flex items-center gap-2.5 mb-4">
                <div class="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
                  <i class="fi fi-rr-boxes text-base"></i>
                </div>
                <div>
                  <h2 class="text-base font-bold text-slate-800">{{ "Product Mixture" | translate }}</h2>
                  <p class="text-xs text-slate-500">{{ "Customize individual names for products included in this mixture." | translate }}</p>
                </div>
              </div>

              <div class="divide-y divide-slate-200 border border-slate-200 rounded-lg overflow-hidden bg-slate-50/30">
                <div
                  *ngFor="let mixtureProduct of productData.mixture_data"
                  class="bg-white p-3.5 transition duration-150 hover:bg-slate-50/80 mixture-product-list"
                  [class]="
                    mixtureIsSaving &&
                    editMixtureProductId === +mixtureProduct.p_id
                      ? '!opacity-30 pointer-events-none'
                      : ''
                  "
                >
                  <form (submit)="$event.preventDefault()">
                    <div class="flex items-center justify-between gap-3">
                      <div class="flex items-center gap-3 flex-1 min-w-0">
                        <img
                          [src]="mixtureProduct.image"
                          class="h-11 w-11 rounded-lg object-cover border border-slate-200 shadow-2xs flex-shrink-0"
                          alt="Mixture item"
                        />
                        <div class="flex-1 min-w-0">
                          <input
                            name="mixture-product-name"
                            class="mixture-product-name bg-transparent border-slate-300 text-slate-800 text-sm font-medium p-1.5 rounded w-full transition"
                            [class]="
                              isEditMixtureProductName(+mixtureProduct.p_id)
                                ? 'border-b-2 !bg-indigo-50/70 border-indigo-500 focus:outline-none'
                                : 'border-transparent'
                            "
                            [(ngModel)]="mixtureProduct.product_name"
                            [readonly]="
                              !isEditMixtureProductName(+mixtureProduct.p_id)
                            "
                          />
                        </div>
                      </div>

                      <div class="mixture-product-action flex items-center gap-1.5 flex-shrink-0">
                        <ng-container
                          *ngIf="!isEditMixtureProductName(+mixtureProduct.p_id)"
                        >
                          <button
                            type="button"
                            class="inline-flex rounded-lg bg-slate-100 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-300 justify-center items-center h-8 w-8 text-slate-600 hover:text-indigo-600 transition"
                            #tooltip="matTooltip"
                            matTooltip="{{ 'Update product mixture name' | translate }}"
                            (click)="
                              enableEditMixtureProduct(
                                +mixtureProduct.p_id,
                                $event
                              )
                            "
                          >
                            <i class="fi fi-rs-pencil text-xs"></i>
                          </button>
                        </ng-container>

                        <ng-container
                          *ngIf="isEditMixtureProductName(+mixtureProduct.p_id)"
                        >
                          <button
                            type="button"
                            class="inline-flex rounded-lg bg-emerald-600 hover:bg-emerald-700 justify-center items-center h-8 w-8 text-white transition shadow-2xs"
                            #tooltip="matTooltip"
                            matTooltip="{{ 'Save changes' | translate }}"
                            (click)="
                              saveMixtureProductNameChange(
                                +mixtureProduct.p_id,
                                $event
                              )
                            "
                          >
                            <i class="fi fi-rs-check text-xs"></i>
                          </button>
                          <button
                            type="button"
                            class="inline-flex rounded-lg bg-amber-500 hover:bg-amber-600 justify-center items-center h-8 w-8 text-white transition shadow-2xs"
                            #tooltip="matTooltip"
                            matTooltip="{{ 'Discard changes' | translate }}"
                            (click)="
                              discardMixtureProductNameChange(
                                +mixtureProduct.p_id,
                                $event
                              )
                            "
                          >
                            <i class="fi fi-rs-cross-small text-xs"></i>
                          </button>
                        </ng-container>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            <!-- CARD 4: SEO & Page Metadata -->
            <div class="bg-white rounded-xl border border-slate-200/80 shadow-xs p-5 sm:p-6">
              <div class="flex items-center gap-2.5 mb-4">
                <div class="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                  <i class="fi fi-rr-search text-base"></i>
                </div>
                <div>
                  <h2 class="text-base font-bold text-slate-800">{{ "Search Engine Optimization (SEO)" | translate }}</h2>
                  <p class="text-xs text-slate-500">{{ "Configure page titles, URL slug, and search meta descriptions." | translate }}</p>
                </div>
              </div>

              <div class="space-y-4">
                <!-- Page Title -->
                <div>
                  <label for="product-page-title" class="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                    {{ "Product Page Title" | translate }}
                  </label>
                  <input
                    type="text"
                    name="product-page-title"
                    id="product-page-title"
                    class="h-10 border border-slate-300 focus:border-indigo-500 rounded-lg px-3.5 w-full bg-slate-50/50 text-sm text-slate-800 transition"
                    [(ngModel)]="productData.metadata.page_title"
                    placeholder="{{ 'Enter SEO page title...' | translate }}"
                  />
                </div>

                <!-- Page URL Slug -->
                <div>
                  <label for="product-page-url" class="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                    {{ "Product Page URL" | translate }}
                  </label>
                  <div class="flex items-center border border-slate-300 rounded-lg bg-slate-50 overflow-hidden focus-within:border-indigo-500 transition">
                    <span class="px-3 py-2 text-xs text-slate-500 border-r border-slate-200 bg-slate-100 whitespace-nowrap font-mono">
                      {{ storeUrlPrefix }}
                    </span>
                    <input
                      type="text"
                      name="product-page-url"
                      id="product-page-url"
                      class="h-10 flex-1 border-0 px-3 bg-transparent text-sm text-slate-800 font-medium focus:outline-none"
                      [(ngModel)]="productUrl"
                      (input)="onProductUrlInput($event)"
                      placeholder="product-url-slug"
                    />
                  </div>
                </div>

                <!-- Meta Description -->
                <div>
                  <div class="flex items-center justify-between mb-1">
                    <label for="meta-description" class="block text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      {{ "Meta Description" | translate }}
                    </label>
                    <button
                      type="button"
                      (click)="
                        showAIProductSEODesc({
                          product_id: productData.product_id,
                          desc: 'seo_description - for product ' + productData.product_name
                        })
                      "
                      [ngClass]="enableAIAssistant !== true ? 'disabled' : ''"
                      class="ai-assistant-button"
                    >
                      <i class="fas fa-magic"></i>
                      <span>{{ "AI Assistant" | translate }}</span>
                    </button>
                  </div>
                  <textarea
                    name="meta-description"
                    id="meta-description"
                    class="border border-slate-300 focus:border-indigo-500 rounded-lg p-3 w-full h-24 bg-slate-50/50 text-sm text-slate-800 resize-y transition"
                    [(ngModel)]="productData.metadata.description"
                    placeholder="{{ 'Brief summary for search engine results...' | translate }}"
                  ></textarea>
                </div>

                <!-- Meta Keywords -->
                <div>
                  <div class="flex items-center justify-between mb-1">
                    <label for="meta-keywords" class="block text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      {{ "Meta Keywords" | translate }}
                    </label>
                    <button
                      type="button"
                      (click)="
                        showAIProductSEOKeyword({
                          product_id: productData.product_id,
                          desc: 'seo_keywords - for product ' + productData.product_name
                        })
                      "
                      class="ai-assistant-button disabled"
                      disabled
                    >
                      <i class="fas fa-magic"></i>
                      <span>{{ "AI Assistant (Soon)" | translate }}</span>
                    </button>
                  </div>
                  <input
                    type="text"
                    name="meta-keywords"
                    id="meta-keywords"
                    class="h-10 border border-slate-300 focus:border-indigo-500 rounded-lg px-3.5 w-full bg-slate-50/50 text-sm text-slate-800 transition"
                    [(ngModel)]="productData.metadata.keywords"
                    placeholder="{{ 'comma, separated, keywords' | translate }}"
                  />
                </div>
              </div>
            </div>
          </div>


          <!-- RIGHT COLUMN (Sidebar - 1/3 Width) -->
          <div class="lg:col-span-1 space-y-6">

            <!-- SIDEBAR CARD 1: Media Gallery (Thumbnail + Stacked Gallery Photos) -->
            <div class="bg-white rounded-xl border border-slate-200/80 shadow-xs p-5 sm:p-6">
              <div class="flex items-center gap-2.5 mb-4">
                <div class="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <i class="fi fi-rr-picture text-base"></i>
                </div>
                <div>
                  <h2 class="text-base font-bold text-slate-800">{{ "Product Media" | translate }}</h2>
                  <p class="text-xs text-slate-500">{{ "Thumbnail and product photos." | translate }}</p>
                </div>
              </div>

              <!-- Primary Thumbnail -->
              <div class="mb-4 pb-4 border-b border-slate-100">
                <div class="flex items-center justify-between mb-2">
                  <label class="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    {{ "Main Thumbnail" | translate }}
                  </label>
                  <span class="text-[10px] font-semibold text-blue-600 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full">
                    {{ "Primary" | translate }}
                  </span>
                </div>
                <div class="bg-slate-50 border border-slate-200 rounded-lg p-2.5 flex flex-col items-center justify-center text-center">
                  <media-image-selector
                    [(imgURL)]="imgURL_Primary"
                    [(mediaID)]="mediaPhotoId"
                  ></media-image-selector>
                </div>
                <p class="text-[10px] text-slate-400 mt-1.5 text-center">
                  {{ "584px × 584px recommended (max 5MB)" | translate }}
                </p>
              </div>

              <!-- Product Gallery Photos (Clean Stacked Layout) -->
              <div>
                <label class="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2.5">
                  {{ "Gallery Photos" | translate }}
                </label>

                <div class="grid grid-cols-1 gap-3">
                  <div class="bg-slate-50 border border-slate-200 rounded-lg p-2.5 flex flex-col items-center justify-center text-center">
                    <span class="block text-xs font-medium text-slate-600 mb-1.5">{{ "Product Photo 1" | translate }}</span>
                    <media-image-selector
                      [(imgURL)]="imgURLs[0]"
                      [(mediaID)]="mediaPhotoId"
                    ></media-image-selector>
                  </div>

                  <div class="bg-slate-50 border border-slate-200 rounded-lg p-2.5 flex flex-col items-center justify-center text-center">
                    <span class="block text-xs font-medium text-slate-600 mb-1.5">{{ "Product Photo 2" | translate }}</span>
                    <media-image-selector
                      [ngClass]="{ disabled: !imgURLs[0] }"
                      [(imgURL)]="imgURLs[1]"
                      [(mediaID)]="mediaPhotoId"
                    ></media-image-selector>
                  </div>

                  <div class="bg-slate-50 border border-slate-200 rounded-lg p-2.5 flex flex-col items-center justify-center text-center">
                    <span class="block text-xs font-medium text-slate-600 mb-1.5">{{ "Product Photo 3" | translate }}</span>
                    <media-image-selector
                      [ngClass]="{ disabled: !imgURLs[1] }"
                      [(imgURL)]="imgURLs[2]"
                      [(mediaID)]="mediaPhotoId"
                    ></media-image-selector>
                  </div>
                </div>
              </div>
            </div>

            <!-- SIDEBAR CARD 2: Inventory & Quantity Control -->
            <div class="bg-white rounded-xl border border-slate-200/80 shadow-xs p-5 sm:p-6">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center">
                  <i class="fi fi-rr-stats text-base"></i>
                </div>
                <div>
                  <h2 class="text-base font-bold text-slate-800">{{ "Display Quantity" | translate }}</h2>
                  <p class="text-xs text-slate-500">{{ "Set initial product purchase quantity." | translate }}</p>
                </div>
              </div>

              <div class="product-quantity-box flex flex-col items-center justify-center text-center pt-2">
                <div class="qty-box">
                  <div class="minus-btn" (click)="minusQty()">
                    <i class="fi fi-rr-minus-small"></i>
                  </div>
                  <input
                    type="text"
                    name="quantity"
                    [value]="defaultQuantity"
                    class="qty-input"
                    readonly
                  />
                  <div class="plus-btn" (click)="plusQty()">
                    <i class="fi fi-rr-plus-small"></i>
                  </div>
                </div>

                <div class="notice-msg mt-2 text-xs text-red-500 flex items-center justify-center gap-1 font-medium" *ngIf="reachMaxQty">
                  <i class="fi fi-rr-info"></i>
                  <span>{{ "Reached Maximum Quantity" | translate }}</span>
                </div>
              </div>
            </div>

            <!-- SIDEBAR CARD 3: Quick Info Summary -->
            <div class="bg-slate-100/70 rounded-xl border border-slate-200/80 p-4">
              <div class="flex items-center justify-between text-xs text-slate-600 mb-2">
                <span>{{ "Product ID" | translate }}:</span>
                <span class="font-mono font-semibold text-slate-800">#{{ productId }}</span>
              </div>
              <div class="flex items-center justify-between text-xs text-slate-600 mb-2">
                <span>{{ "Language" | translate }}:</span>
                <span class="font-semibold text-slate-800 uppercase">{{ languageCode }}</span>
              </div>
              <div class="flex items-center justify-between text-xs text-slate-600" *ngIf="countryCode">
                <span>{{ "Country" | translate }}:</span>
                <span class="font-semibold text-slate-800 uppercase">{{ countryCode }}</span>
              </div>
            </div>

          </div>

        </div>
      </div>
    </ng-template>
  </div>
</div>
